import React, { useEffect, useRef, useState } from "react";
import Avatar from "@mui/material/Avatar";
import { Link, useNavigate } from "react-router-dom";
import { TbArrowBackUp } from "react-icons/tb";
import apiClient from "../api/apiClient";
import { handleErrors } from "../modules/handleErrors";
import { toast } from "react-toastify";
import { useIndexPageData } from "../contextapi/Indexpagedata";
import { useSelector } from "react-redux";
import LoadingSpinner from "../components/LoadingSpinner";
import { Image } from "primereact/image";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Tooltip } from "@mui/material";
import StripeCheckoutModal from "../modals/StripeCheckoutModal";
import CheckoutAuthModal from "../modals/CheckoutAuthModal";
import { formatPrice, getActiveSale, getSalePriceDisplay } from "../utils/saleUtils";
import SalePriceLabel from "../components/SalePriceLabel";
import { useGuardedAsync } from "../hooks/useGuardedAsync";

const Bundle = () => {
  const currentUrl = window.location.origin;
  const { fetchcart, profiledata } = useIndexPageData();
  const { isLoggedIn, role } = useSelector((state) => state.auth);
  const isAdmin = role === "A";

  const getCurrentURLParameters = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const params = {};
    for (const [key, value] of searchParams) {
      params[key] = value;
    }
    return params;
  };

  const { id } = getCurrentURLParameters();
  const navigate = useNavigate();
  const [mockupData, setMockupData] = useState({});
  const [loader, setLoader] = useState(false);
  const [buyLoader, setBuyLoader] = useState(false);
  const { loading: cartLoader, run: runAddToCart } = useGuardedAsync();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(0);
  const [showCheckoutAuth, setShowCheckoutAuth] = useState(false);
  const [checkoutAuthIntent, setCheckoutAuthIntent] = useState("buy");
  const pendingCheckoutAction = useRef(null);
  const [checkedItems, setCheckedItems] = useState([]);
  // Function to copy mockup ID to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Mockup ID copied to clipboard!");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("Mockup ID copied to clipboard!");
      } catch (err) {
        toast.error("Failed to copy to clipboard");
      }
      document.body.removeChild(textArea);
    }
  };

  const fetchMockup = async () => {
    setLoader(true);
    let result;
    result = await apiClient.get(`/bundle-listings/${id}/`);

    setLoader(false);
    result.data = {
      ...result.data,
      thumbnails: [
        ...(result?.data?.bundle_thumbnails || []),
        ...(result?.data?.listings || [])
          .map((listing) => listing?.compressed_file)
          .filter(Boolean)
          .map((url) => ({ image: url })),
      ],
    };
    if (result.ok) setMockupData(result?.data);
  };

  useEffect(() => {
    if (id) {
      fetchMockup();
    }
  }, [id]);

  // Preload adjacent images for faster navigation
  useEffect(() => {
    if (mockupData.thumbnails?.length > 0) {
      const preloadImages = [];

      // Preload next image
      if (currentIndex < mockupData.thumbnails.length - 1) {
        const nextImg = document.createElement("img");
        nextImg.src = mockupData.thumbnails[currentIndex + 1]?.image;
        preloadImages.push(nextImg);
      }

      // Preload previous image
      if (currentIndex > 0) {
        const prevImg = document.createElement("img");
        prevImg.src = mockupData.thumbnails[currentIndex - 1]?.image;
        preloadImages.push(prevImg);
      }

      // Preload main image if available
      if (mockupData.file) {
        const mainImg = document.createElement("img");
        mainImg.src = mockupData.file;
        preloadImages.push(mainImg);
      }
    }
  }, [currentIndex, mockupData.thumbnails, mockupData.file]);

  const currentImageSrc =
    currentIndex === -1
      ? mockupData.file
      : mockupData.thumbnails?.[currentIndex]?.image;

  const addBundleToCart = (bundleId) => {
    runAddToCart(async () => {
      const result = await apiClient.post("/cartitems/", {
        product_id: bundleId,
        modelType: "bundle",
      });
      if (!result.ok) {
        handleErrors(result.data);
        return;
      }
      toast.success("Item successfully added to your cart!");
      fetchMockup();
      fetchcart();
    });
  };

  const openBundleCheckout = () => {
    setCheckedItems([
      {
        product_id: id,
        modelType: "bundle",
      },
    ]);
    setShowStripeModal(true);
  };

  const requireCheckoutAuth = (action) => {
    pendingCheckoutAction.current = action;
    setCheckoutAuthIntent(action === "cart" ? "cart" : "buy");
    setShowCheckoutAuth(true);
  };

  const handleAddToBag = (bundleId) => {
    if (!isLoggedIn) return requireCheckoutAuth("cart");
    addBundleToCart(bundleId);
  };

  const handlePayment = () => {
    if (!isLoggedIn) return requireCheckoutAuth("buy");
    openBundleCheckout();
  };

  const handleCheckoutAuthClose = () => {
    pendingCheckoutAction.current = null;
    setShowCheckoutAuth(false);
  };

  const handleCheckoutAuthSuccess = () => {
    pendingCheckoutAction.current = null;
    setShowCheckoutAuth(false);
    navigate("/cart");
  };

  const handleNextThumbnail = () => {
    if (currentIndex < (mockupData.thumbnails?.length || 0) - 1) {
      setImageLoading(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setImageLoading(false);
      }, 500);
    }
  };

  const handlePreviousThumbnail = () => {
    if (currentIndex > 0) {
      setImageLoading(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex - 1);
        setImageLoading(false);
      }, 500);
    }
  };

  const isPrevDisabled = currentIndex <= 0;
  const isNextDisabled =
    currentIndex >= (mockupData.thumbnails?.length || 0) - 1;
  const checkoutSale = getSalePriceDisplay(
    mockupData?.price,
    getActiveSale(mockupData)
  );
  const checkoutProduct = {
    title: mockupData?.title,
    price: formatPrice(checkoutSale.displayDiscounted ?? mockupData?.price),
    image:
      mockupData?.thumbnails?.[0]?.image ||
      mockupData?.file ||
      mockupData?.compressed_file,
    productId: mockupData?.id || id,
    modelType: "bundle",
  };

  return (
    <>
      {loader ? (
        <div className="d-flex justify-content-center mt-5">
          <LoadingSpinner color="#FF3E1F" />
        </div>
      ) : (
        <section className="mockup py-5 my-md-5 my-3">
          <div className="container">
            <div className="row">
              <div className="col-md-5">
                <div className="bg-white shadow px-0 pb-2 py-0 rounded-4  overflow-hidden">
                  <div
                    className="d-flex justify-content-center slider-container position-relative"
                    style={{ minHeight: "400px", width: "100%" }}
                  >
                    {imageLoading ? (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{
                          backgroundColor: "white",
                          width: "100%",
                          height: "auto",
                          minHeight: "400px",
                          objectFit: "contain",
                          maxWidth: "100%",
                        }}
                      >
                        <LoadingSpinner color="#FF3E1F" />
                      </div>
                    ) : (
                      <>
                        <Image
                          style={{
                            objectFit: "contain",
                            width: "100%",
                            height: "auto",
                            maxWidth: "100%",
                          }}
                          src={currentImageSrc}
                          alt="Image"
                          preview
                        />
                        <div
                          className="position-absolute z-3"
                          style={{
                            top: "10px",
                            right: "10px",
                            backgroundColor: "#C93312",
                            color: "white",
                            borderRadius: "50%",
                            width: "50px",
                            height: "50px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                          }}
                        >
                          <img
                            src="../../assets/images/BundleIcon-removebg-preview.png"
                            alt="bundle icon"
                            style={{
                              width: "25px",
                              height: "25px",
                              objectFit: "contain",
                              borderRadius: "50%",
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="d-flex justify-content-end gap-3 mt-4">
                    <div
                      className={`slider-button ${
                        isPrevDisabled ? "disabled" : ""
                      }`}
                    >
                      <ArrowBack
                        sx={{ fontSize: "30px" }}
                        onClick={handlePreviousThumbnail}
                        style={{
                          cursor: isPrevDisabled ? "not-allowed" : "pointer",
                        }}
                      />
                    </div>
                    <div
                      className={`slider-button ${
                        isNextDisabled ? "disabled" : ""
                      }`}
                    >
                      <ArrowForward
                        sx={{ fontSize: "30px" }}
                        onClick={handleNextThumbnail}
                        style={{
                          cursor: isNextDisabled ? "not-allowed" : "pointer",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6 ms-md-5 mt-md-0 mt-4">
                <TbArrowBackUp
                  style={{ fontSize: "32px", cursor: "pointer" }}
                  className="back-arrow-new shadow me-2 mb-3 rounded-5 p-1 arrow-button"
                  onClick={() => navigate(-1)}
                />
                <h2 className="mb-0">{mockupData?.title}</h2>
                <p className="mb-2 d-flex align-items-center gap-2">
                  ID: <span>{mockupData?.id}</span>
                  <Tooltip title="Copy to clipboard">
                    <ContentCopyIcon
                      onClick={() => copyToClipboard(mockupData?.id)}
                      style={{
                        fontSize: "16px",
                        cursor: "pointer",
                        color: "#C93312",
                        transition: "color 0.2s ease",
                      }}
                      onMouseEnter={(e) => (e.target.style.color = "#A02812")}
                      onMouseLeave={(e) => (e.target.style.color = "#C93312")}
                    />
                  </Tooltip>
                </p>
                <p>{mockupData?.short_description}</p>
                {mockupData?.price != null && mockupData?.price !== "" && (() => {
                  const saleInfo = getActiveSale(mockupData);
                  const salePrice = getSalePriceDisplay(mockupData?.price, saleInfo);
                  if (salePrice.displayDiscounted == null) return null;
                  return (
                    <h2 className="fw-bold mt-3 d-flex align-items-baseline flex-wrap" style={{ fontSize: "42px" }}>
                      <SalePriceLabel
                        salePrice={salePrice}
                        strikeStyle={{
                          fontSize: "32px",
                          fontWeight: "normal",
                          color: "inherit",
                          textDecoration: "line-through",
                          marginRight: "8px",
                        }}
                      />
                    </h2>
                  );
                })()}

                <div className="d-flex gap-3 mt-4 flex-wrap">
                  {(mockupData?.is_bought || isAdmin) && (
                    <>
                      <div class="card shadow-sm border-0 rounded-4 bg-light text-center p-4">
                        <h6 class="fw-semibold text-dark mb-0">
                          ✨ Click any of the items below to use
                        </h6>
                      </div>

                      <div
                        className="d-flex gap-3 flex-wrap"
                        style={{
                          overflowX: "auto",
                        }}
                      >
                        {mockupData?.listings?.length &&
                          mockupData?.listings?.map((listing, idx) => (
                            <div
                              key={idx}
                              className="rounded d-flex flex-column align-items-center"
                              style={{
                                cursor: "pointer",
                                width: "100px",
                              }}
                              onClick={() =>
                                navigate(`/mockup-editor?id=${listing?.id}`)
                              }
                              // title={listing?.title || `Item ${idx}`}
                            >
                              <Tooltip title={listing?.title}>
                                <div
                                  style={{
                                    width: "100px",
                                    height: "100px",
                                    overflow: "hidden",
                                    borderRadius: "6px",
                                  }}
                                >
                                  
                                  <img
                                    src={listing?.use_main_file_as_thumbnail ? listing?.compressed_file : listing?.thumbnails?.[0]?.image}
                                    alt={listing?.title || `Item ${idx}`}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      display: "block",
                                    }}
                                  />
                                </div>
                              </Tooltip>
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                  {!mockupData?.is_bought &&
                    !profiledata?.is_suspended &&
                    !isAdmin && (
                      <button
                        className="btn red-btn rounded-3"
                        disabled={buyLoader}
                        onClick={handlePayment}
                      >
                        {buyLoader ? "Buy..." : "Buy"}
                      </button>
                    )}
                  {!(mockupData?.is_cart ||
                    mockupData?.is_bought ||
                    profiledata?.is_suspended ||
                    isAdmin) && (
                    <button
                      className="btn red-btn rounded-3"
                      disabled={cartLoader}
                      onClick={() => handleAddToBag(mockupData?.id)}
                    >
                      {cartLoader ? "Adding..." : "Add to cart"}
                    </button>
                  )}
                </div>
                <hr />
                <div className="details  d-none d-lg-block">
                  <h2>Details</h2>
                  <div className="d-flex gap-3">
                    {mockupData.ai_models === "AI" && (
                      <img
                        style={{ width: "40px" }}
                        src="../assets/images/robot.png"
                        title={
                          mockupData.ai_models === "Mix"
                            ? "This mockup is created by using mix of techniques."
                            : "This mockup is AI generated."
                        }
                        alt=""
                      />
                    )}
                    {mockupData.ai_models === "Real" && (
                      <img
                        style={{ width: "40px" }}
                        src="../assets/images/camera.png"
                        title={
                          mockupData.ai_models === "Mix"
                            ? "This mockup is created by using mix of techniques."
                            : "This mockup represents a real product."
                        }
                        alt=""
                      />
                    )}
                    {mockupData.ai_models === "3D" && (
                      <img
                        style={{ width: "40px" }}
                        src="../assets/images/cube.png"
                        title={
                          mockupData.ai_models === "Mix"
                            ? "This mockup is created by using mix of techniques."
                            : "This mockup is generated in 3D software."
                        }
                        alt=""
                      />
                    )}
                    {mockupData.ai_models === "Mix" && (
                      <img
                        style={{ width: "40px" }}
                        src="../assets/images/Frame1.png"
                        title={
                          mockupData.ai_models === "Mix"
                            ? "This mockup is created by using mix of techniques."
                            : "This mockup is generated in 3D software."
                        }
                        alt=""
                      />
                    )}
                  </div>
                  <div className="row mt-4">
                    <div className="col-md-7 order-1 order-md-0">
                      <ul className="p-0 m-0">
                        <li>
                          <b
                            style={{ width: "30%" }}
                            className="d-inline-block"
                          >
                            Type:
                          </b>{" "}
                          <span className="ms-4">{"Bundle"}</span>
                        </li>

                        <li>
                          <b
                            style={{ width: "30%" }}
                            className="d-inline-block"
                          >
                            Mockups:
                          </b>{" "}
                          <span className="ms-4">
                            {mockupData?.listings_count}
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-4">
                      <div className="d-flex gap-3">
                        <div>
                          <p>
                            Mockup By: <br />{" "}
                            <Link
                              to={`/browse-mockups?name=${mockupData?.user_id || mockupData?.creator}`}
                              style={{ color: "#000000e8" }}
                            >
                              {mockupData?.user_name}
                            </Link>
                          </p>
                        </div>
                        <Avatar
                          alt="Remy Sharp"
                          src={mockupData?.user_profile}
                        />
                      </div>
                    </div>
                    {/* <div className="mb-3">
                      <b style={{ width: "17%" }} className="d-inline-block">
                        Product link
                      </b>{" "}
                      <Link
                        to={mockupData?.product_link}
                        target="_blank"
                        className="ms-4 text-decoration-none"
                        style={{ color: "#000" }}
                      >
                        {mockupData?.product_link}
                      </Link>
                    </div> */}
                    <div>
                      <h4>Description</h4>
                      <p>{mockupData?.long_description}</p>
                      {/* <div className="d-flex justify-content-end mt-4">
                        <a
                          href={`mailto:report@mockee.io?subject=Report a mockup [${id}]&body=Describe what is wrong with this mockup, and why it should be removed or changed:%0D%0A%0D%0A`}
                          style={{
                            color: "#C93312",
                            textDecoration: "underline",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          Report a mockup
                        </a>
                      </div> */}
                    </div>
                  </div>
                </div>
                <div className="details  d-lg-none">
                  <h2>Details</h2>

                  <div>
                    <h4>Description</h4>
                    <p>{mockupData?.long_description}</p>
                  </div>
                  <div className="row mt-4">
                    <div className="col-md-7 order-1 order-md-0">
                      <ul className="p-0 m-0">
                        <li className="d-flex justify-content-between align-items-center mb-2">
                          <b className="d-inline-block align-self-start">
                            Type:
                          </b>{" "}
                          <span className="text-end">{"Bundle"}</span>
                        </li>

                        <li className="d-flex justify-content-between align-items-center mb-2">
                          <b className="d-inline-block align-self-start">
                            Mockups:
                          </b>{" "}
                          <span className="text-end">
                            {mockupData?.listings_count}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center ">
                  <div className="d-flex gap-3 align-items-center">
                    <div>
                      <p>
                        Mockup By: <br />{" "}
                        <Link
                          to={`/browse-mockups?name=${mockupData?.user_id || mockupData?.creator}`}
                          style={{ color: "#000000e8" }}
                        >
                          {mockupData?.user_name}
                        </Link>
                      </p>
                    </div>
                    <Avatar alt="Remy Sharp" src={mockupData?.user_profile} />
                  </div>
                  <div className="d-flex justify-content-end ">
                    <a
                      href={`mailto:report@mockee.io?subject=Report a mockup [${id}]&body=Describe what is wrong with this mockup, and why it should be removed or changed:%0D%0A%0D%0A`}
                      style={{
                        color: "#C93312",
                        textDecoration: "underline",
                        fontSize: "14px",
                        cursor: "pointer",
                      }}
                    >
                      Report a mockup
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      <StripeCheckoutModal
        open={showStripeModal}
        onClose={() => setShowStripeModal(false)}
        items={checkedItems}
        subtotal={checkoutSale.displayDiscounted ?? mockupData?.price}
      />
      <CheckoutAuthModal
        show={showCheckoutAuth}
        onHide={handleCheckoutAuthClose}
        onAuthenticated={handleCheckoutAuthSuccess}
        product={checkoutProduct}
        intent={checkoutAuthIntent}
      />
    </>
  );
};

export default Bundle;
